import type {
  RecommendedSellPrice,
  SellPortCandidate,
  SellPriceCandidatesByPrepayment,
  TransportSurcharges,
} from '@/types/bestPrice';

/** Оборудование продавца, влияющее на надбавки к тарифу перевозки. */
export interface SellEquipment {
  readonly hasScales: boolean;
  readonly hasTrawl: boolean;
  readonly hasTrailer: boolean;
}

/**
 * Суммарная надбавка к тарифу перевозки (₽/т·км) для порта при заданном оборудовании. Правила совпадают
 * с сервером (refineTariff): весы — всегда; трал — только на маршрутах >= minDistanceKm; самосвал — только
 * при наличии трала и >= minDistanceKm.
 */
function surchargeRatePerTonKm(distance: number, eq: SellEquipment, rates: TransportSurcharges): number {
  let rate = 0;
  if (!eq.hasScales) {
    rate += rates.noScalesRate;
  }
  const farRoute = distance >= rates.minDistanceKm;
  if (!eq.hasTrawl && farRoute) {
    rate += rates.noTralRate;
  }
  if (eq.hasTrawl && !eq.hasTrailer && farRoute) {
    rate += rates.noDumpTruckRate;
  }
  return rate;
}

/**
 * FCA кандидата с учётом оборудования: из цены при всём включённом оборудовании вычитаем надбавку в ₽/т
 * (ставка × дистанция, округление до рубля — как перевозка на сервере). Без ставок или дистанции надбавку
 * не начисляем (fallback — цена при всём ВКЛ; сервер всё равно пересчитает авторитетно при заключении).
 */
export function adjustedSellFca(
  candidate: SellPortCandidate,
  eq: SellEquipment,
  rates: TransportSurcharges | undefined,
): number {
  if (!rates || candidate.distance == null || candidate.distance <= 0) {
    return candidate.price;
  }
  const extraPerTon = Math.round(surchargeRatePerTonKm(candidate.distance, eq, rates) * candidate.distance);
  return candidate.price - extraPerTon;
}

/**
 * Пересчитывает кандидатов по типам оплаты в строки ориентира с учётом оборудования: по каждому типу оплаты
 * берём порт с максимальной FCA продавцу (нетто), строки сортируем по цене ↓ (самая выгодная — первой).
 * Клиент делает это мгновенно при переключении оборудования, без повторного запроса.
 */
export function recomputeSellPriceRows(
  candidatesByPrepayment: SellPriceCandidatesByPrepayment | undefined,
  eq: SellEquipment,
  rates: TransportSurcharges | undefined,
): RecommendedSellPrice[] {
  if (!candidatesByPrepayment) {
    return [];
  }
  const rows: RecommendedSellPrice[] = [];
  for (const [prepaymentType, candidates] of Object.entries(candidatesByPrepayment)) {
    let best: RecommendedSellPrice | null = null;
    for (const candidate of candidates) {
      const price = adjustedSellFca(candidate, eq, rates);
      if (price <= 0) {
        continue;
      }
      if (!best || price > best.price) {
        best = {
          prepaymentType,
          price,
          buyerAddressName: candidate.buyerAddressName,
          purchaseOrderId: candidate.purchaseOrderId,
          purchaseOrderVersion: candidate.purchaseOrderVersion,
        };
      }
    }
    if (best) {
      rows.push(best);
    }
  }
  return rows.sort((a, b) => b.price - a.price);
}
